/**
 * Ecommerce dataLayer emitter for GA4 / GTM.
 *
 * Emits standard GA4 ecommerce events regardless of whether the underlying
 * analytics scripts have loaded — the events sit on window.dataLayer until
 * gtag/gtm consumes them.  This means:
 *
 *   * We fire events consistently even before consent is granted.  When
 *     consent lands, gtag config replays.
 *   * Server-verified purchase events are idempotent per `transaction_id`
 *     via a sessionStorage sentinel — a shopper refreshing the success page
 *     will not fire `purchase` twice.
 *
 * No PII is emitted — only product/SKU IDs and monetary values.
 *
 * Behavior analytics mirror (Microsoft Clarity)
 * ---------------------------------------------
 * Every public tracker below mirrors the event NAME (never parameters) to
 * `clarity('event', name)` via the whitelist in `./clarity.js`. Clarity
 * itself is denied-by-default and only records once consent is granted;
 * mirroring the name pre-consent is safe because the CDN sits idle until
 * `analytics_Storage: 'granted'` fires.
 */

import { clarityTrack } from "./clarity";

const KEY_PURCHASE_FIRED = "mps_ga4_purchase_fired_v1";
// Per-tab dedupe stores for the drop-off events (schema below). These
// sentinels prevent a re-render burst / focus flicker / rate re-click
// from double-emitting the same event within one checkout attempt.
const KEY_ADD_SHIP_FIRED = "mps_ga4_add_shipping_info_fired_v1";
const KEY_ADD_PAY_FIRED  = "mps_ga4_add_payment_info_fired_v1";
const KEY_SEARCH_LAST    = "mps_ga4_search_last_v1";

function push(event, ecommerce) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  // Clear the previous ecommerce object so successive events don't blend.
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({ event, ecommerce });
}

function toItem(product, quantity = 1) {
  if (!product) return null;
  return {
    item_id:       product.public_sku || product.id,
    item_name:     product.public_display_name || product.product_name,
    item_brand:    "Medical Peptide Store",
    item_category: product.research_category || product.category || undefined,
    item_variant:  product.strength || undefined,
    price:         product.retail_price_cents != null
                    ? +(product.retail_price_cents / 100).toFixed(2)
                    : undefined,
    quantity,
  };
}

export function trackViewItem(product) {
  const item = toItem(product);
  if (!item) return;
  push("view_item", {
    currency: (product.currency || "USD").toUpperCase(),
    value:    item.price,
    items:    [item],
  });
  clarityTrack("view_item");
}

export function trackAddToCart(product, quantity = 1) {
  const item = toItem(product, quantity);
  if (!item) return;
  push("add_to_cart", {
    currency: (product.currency || "USD").toUpperCase(),
    value:    (item.price || 0) * quantity,
    items:    [item],
  });
  clarityTrack("add_to_cart");
}

export function trackViewCart(cart) {
  if (!cart) return;
  const items = (cart.items || []).map((li) => ({
    item_id:      li.public_sku || li.sku || li.product_id,
    item_name:    li.public_display_name || li.name,
    item_variant: li.strength || undefined,
    item_brand:   "Medical Peptide Store",
    price:        li.unit_amount_cents != null
                    ? +(li.unit_amount_cents / 100).toFixed(2)
                    : undefined,
    quantity:     li.quantity,
  }));
  push("view_cart", {
    currency: (cart.currency || "USD").toUpperCase(),
    value:    cart.subtotal_cents != null
                ? +(cart.subtotal_cents / 100).toFixed(2)
                : undefined,
    items,
  });
  clarityTrack("view_cart");
}

export function trackBeginCheckout(cart) {
  if (!cart) return;
  const items = (cart.items || []).map((li) => ({
    item_id:      li.public_sku || li.sku || li.product_id,
    item_name:    li.public_display_name || li.name,
    item_variant: li.strength || undefined,
    item_brand:   "Medical Peptide Store",
    price:        li.unit_amount_cents != null
                    ? +(li.unit_amount_cents / 100).toFixed(2)
                    : undefined,
    quantity:     li.quantity,
  }));
  push("begin_checkout", {
    currency: (cart.currency || "USD").toUpperCase(),
    value:    cart.subtotal_cents != null
                ? +(cart.subtotal_cents / 100).toFixed(2)
                : undefined,
    items,
  });
  clarityTrack("begin_checkout");
}


// ────────────── Phase 28r — GA4 drop-off events + Clarity mirror ──────────────

/**
 * Fire GA4 `remove_from_cart`. Called by `lib/cart.jsx::remove()` with
 * the exact removed line item (SKU + quantity + price at time of removal).
 * Idempotent per call — no dedupe needed because the user's explicit
 * click is the trigger.
 */
export function trackRemoveFromCart(lineItem, quantity) {
  if (!lineItem) return;
  const qty = quantity != null ? quantity : (lineItem.quantity || 1);
  const item = {
    item_id:      lineItem.public_sku || lineItem.sku || lineItem.product_id,
    item_name:    lineItem.public_display_name || lineItem.name,
    item_variant: lineItem.strength || undefined,
    item_brand:   "Medical Peptide Store",
    price:        lineItem.unit_amount_cents != null
                    ? +(lineItem.unit_amount_cents / 100).toFixed(2)
                    : undefined,
    quantity:     qty,
  };
  push("remove_from_cart", {
    currency: "USD",
    value:    (item.price || 0) * qty,
    items:    [item],
  });
  clarityTrack("remove_from_cart");
}

/**
 * Fire GA4 `add_shipping_info` when the shopper picks a Shippo rate on
 * `/checkout/shipping`. Deduped per checkout state via a per-tab
 * sentinel keyed on rate_id + subtotal — re-selecting the same rate on
 * the same cart does not re-emit.
 */
export function trackAddShippingInfo({ items, subtotal_cents, currency = "USD", rate_id, shipping_tier }) {
  if (!Array.isArray(items) || items.length === 0) return;
  try {
    const key = `${rate_id || ""}|${subtotal_cents || 0}`;
    const prev = sessionStorage.getItem(KEY_ADD_SHIP_FIRED);
    if (prev === key) return;
    sessionStorage.setItem(KEY_ADD_SHIP_FIRED, key);
  } catch (_) { /* private-mode / SSR — still emit */ }
  const ga4Items = items.map((li) => ({
    item_id:      li.public_sku || li.sku || li.product_id,
    item_name:    li.public_display_name || li.name,
    item_variant: li.strength || undefined,
    item_brand:   "Medical Peptide Store",
    price:        li.unit_amount_cents != null
                    ? +(li.unit_amount_cents / 100).toFixed(2)
                    : undefined,
    quantity:     li.quantity,
  }));
  push("add_shipping_info", {
    currency:      (currency || "USD").toUpperCase(),
    value:         subtotal_cents != null ? +(subtotal_cents / 100).toFixed(2) : undefined,
    shipping_tier: shipping_tier || undefined,
    items:         ga4Items,
  });
  clarityTrack("add_shipping_info");
}

/**
 * Fire GA4 `add_payment_info` after the Stripe Checkout Session has
 * been successfully created, RIGHT BEFORE `window.location.href =
 * checkout_url`. Payment type is always "Stripe Checkout" (we never
 * expose card brand data). Deduped per Checkout Session id per tab.
 */
export function trackAddPaymentInfo({ items, subtotal_cents, currency = "USD", session_id }) {
  if (!Array.isArray(items) || items.length === 0) return;
  try {
    const prev = sessionStorage.getItem(KEY_ADD_PAY_FIRED);
    if (session_id && prev === session_id) return;
    if (session_id) sessionStorage.setItem(KEY_ADD_PAY_FIRED, session_id);
  } catch (_) { /* private-mode / SSR — still emit */ }
  const ga4Items = items.map((li) => ({
    item_id:      li.public_sku || li.sku || li.product_id,
    item_name:    li.public_display_name || li.name,
    item_variant: li.strength || undefined,
    item_brand:   "Medical Peptide Store",
    price:        li.unit_amount_cents != null
                    ? +(li.unit_amount_cents / 100).toFixed(2)
                    : undefined,
    quantity:     li.quantity,
  }));
  push("add_payment_info", {
    currency:     (currency || "USD").toUpperCase(),
    value:        subtotal_cents != null ? +(subtotal_cents / 100).toFixed(2) : undefined,
    payment_type: "Stripe Checkout",
    items:        ga4Items,
  });
  clarityTrack("add_payment_info");
}

/**
 * Fire GA4 `search`. Query must be ≥3 chars; identical consecutive
 * queries are deduped per tab. The query IS pushed to the dataLayer
 * (GA4 accepts it) but is NEVER mirrored to Clarity — the name-only
 * mirror in `clarity.js` guarantees that.
 */
export function trackSearch(query) {
  const q = typeof query === "string" ? query.trim() : "";
  if (q.length < 3) return;
  try {
    const prev = sessionStorage.getItem(KEY_SEARCH_LAST);
    if (prev === q) return;
    sessionStorage.setItem(KEY_SEARCH_LAST, q);
  } catch (_) { /* private-mode / SSR — still emit */ }
  push("search", { search_term: q });
  clarityTrack("search");
}

// Sanitised checkout-stage vocabulary. Anything else the caller sends is
// coerced to "unknown" so no free-text server error message can leak.
const CHECKOUT_STAGES = new Set([
  "cart",
  "shipping_selection",
  "checkout_session_create",
  "redirect_to_stripe",
  "payment_verification",
  "unknown",
]);
const CHECKOUT_ERROR_CODES = new Set([
  "network_error",
  "quote_expired",
  "quote_missing",
  "validation_error",
  "session_create_failed",
  "session_not_found",
  "verification_timeout",
  "unknown",
]);

/**
 * Fire GA4 `checkout_error` custom event. Only two low-cardinality
 * strings ever leave the client — no exception messages, no server
 * `detail` text, no PII. Callers must map their local failure state
 * onto one of the CHECKOUT_STAGES / CHECKOUT_ERROR_CODES constants; if
 * they don't, we coerce to "unknown" (still fires so drop-off counting
 * stays accurate).
 */
export function trackCheckoutError({ error_stage, error_code } = {}) {
  const stage = CHECKOUT_STAGES.has(error_stage) ? error_stage : "unknown";
  const code  = CHECKOUT_ERROR_CODES.has(error_code) ? error_code : "unknown";
  push("checkout_error", { error_stage: stage, error_code: code });
  clarityTrack("checkout_error");
}

// Sanitised payment-failure vocabulary. Same rules as checkout_error.
const PAYMENT_FAIL_REASONS = new Set([
  "cancelled",
  "verification_failed",
  "session_not_found",
  "network_error",
  "unknown",
]);

/**
 * Fire GA4 `payment_failed` custom event on any verified payment
 * failure / cancel path. Sanitised reason only.
 */
export function trackPaymentFailed({ reason } = {}) {
  const r = PAYMENT_FAIL_REASONS.has(reason) ? reason : "unknown";
  push("payment_failed", { reason: r });
  clarityTrack("payment_failed");
}

/**
 * Emit `purchase` + `purchase_verified` ONCE per Stripe session id, using a
 * sessionStorage sentinel.  Callers pass the FULL server-verified session
 * payload from GET /api/orders/session/{session_id}; we refuse to fire
 * until Stripe reports payment_status === "paid".
 *
 * Two events are pushed in sequence:
 *   1. `purchase`          — canonical GA4 ecommerce shape (GTM listens
 *                             for this to fire the GA4 Purchase tag).
 *   2. `purchase_verified` — same payload, dedicated event name so GTM
 *                             tags that want an explicit server-verified
 *                             signal (Google Ads Enhanced Conversions,
 *                             CAPI mirroring, custom pixels) can trigger
 *                             without listening on the generic
 *                             `purchase` event that some tag templates
 *                             also match on ecommerce-store templates.
 * Both events carry identical `ecommerce` blocks; the sentinel dedupe
 * covers BOTH — either both fire or neither does.
 */
export function trackPurchase(sessionPayload) {
  if (!sessionPayload) return;
  const paid = sessionPayload.payment_status === "paid"
             || sessionPayload.status === "paid";
  if (!paid) return;

  const tx = sessionPayload.order_id
          || sessionPayload.session_id
          || sessionPayload.id;
  if (!tx) return;
  try {
    const fired = JSON.parse(sessionStorage.getItem(KEY_PURCHASE_FIRED) || "[]");
    if (fired.includes(tx)) return;
    fired.push(tx);
    sessionStorage.setItem(KEY_PURCHASE_FIRED, JSON.stringify(fired.slice(-25)));
  } catch (e) {
    // If sessionStorage is unavailable we still want to emit the event.
  }

  const items = (sessionPayload.items || []).map((li) => ({
    item_id:      li.public_sku || li.sku || li.product_id,
    item_name:    li.public_display_name || li.name,
    item_variant: li.strength || undefined,
    item_brand:   "Medical Peptide Store",
    price:        li.unit_amount_cents != null
                    ? +(li.unit_amount_cents / 100).toFixed(2)
                    : undefined,
    quantity:     li.quantity,
  }));
  const ecommerce = {
    transaction_id: tx,
    currency:       (sessionPayload.currency || "USD").toUpperCase(),
    value:          sessionPayload.total_cents != null
                      ? +(sessionPayload.total_cents / 100).toFixed(2)
                      : undefined,
    tax:            sessionPayload.tax_cents != null
                      ? +(sessionPayload.tax_cents / 100).toFixed(2)
                      : undefined,
    shipping:       sessionPayload.shipping_cents != null
                      ? +(sessionPayload.shipping_cents / 100).toFixed(2)
                      : undefined,
    items,
  };
  push("purchase",          ecommerce);
  push("purchase_verified", ecommerce);
  clarityTrack("purchase");
}
